import type { Category } from '../types/category';

export const categories: Category[] = [
  {
    slug: 'residences',
    title: 'Residences',
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/ee27a00a-608b-4c4d-bf92-28b34626a7c2.jpg',
    heroAlt: 'A contemporary cliffside villa with an infinity pool above the sea at sunset',
    tagline: 'Homes that are never listed. Only offered.',
    statement: 'The finest houses rarely change hands in public. We are told first.',
    intro:
      'Villas, penthouses, chalets and estates — for a week, a season or a lifetime. Through owners, developers, family offices and private banks, Superluxury holds a collection of residences that will never appear on a portal, and opens them to members alone.',
    waysHeading: 'To stay, or to own',
    ways: [
      {
        label: 'Concierge & rental',
        title: 'To stay',
        text: 'A house prepared before you arrive — staffed, stocked and arranged to your habits, for as long as you wish to stay.',
        points: ['Villas, penthouses and chalets by the week or season', 'Household staff, chef and security on request', 'The same house, held for you each year'],
      },
      {
        label: 'Acquisition',
        title: 'To own',
        text: 'Off-market homes, sourced in confidence and acquired quietly — with viewings, advisory and negotiation handled on your behalf.',
        points: ['Introductions before a property reaches the market', 'Private viewings, by appointment only', 'Legal, structuring and handover, end to end'],
      },
    ],
    curationHeading: 'How a residence reaches you',
    curation: [
      { title: 'Sourced in confidence', text: 'Owners, developers and family offices bring their homes to us before anyone else.' },
      { title: 'Seen in person', text: 'Every residence is visited by a partner. Most never make it into the collection.' },
      { title: 'Held for members', text: 'What remains is offered to members first — often to members alone.' },
    ],
    accessLine: 'Residences are shown to members, or by single introduction for a specific stay or purchase.',
    collectionHeading: 'Currently held, privately',
    collectionNote: 'A glimpse of the collection. Addresses, prices and owners are shared on introduction.',
    listings: [
      {
        title: 'Clifftop villa with eight suites',
        location: 'Côte d’Azur',
        kind: 'For rent · Summer',
        specs: ['8 suites', '1,200 m²', 'Private cove'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/774943d4-26aa-4230-a9f6-c88db45f8e06.jpg',
        alt: 'A white clifftop villa with a long infinity pool above the sea at golden hour',
      },
      {
        title: 'Full-floor penthouse above the marina',
        location: 'Dubai',
        kind: 'For purchase',
        specs: ['5 bedrooms', '940 m²', 'Private lift'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/85a41a71-edeb-4e18-a895-1c1f9b79cda7.jpg',
        alt: 'A penthouse living room at blue hour overlooking a glittering skyline',
      },
      {
        title: 'Restored finca among the olive groves',
        location: 'Mallorca',
        kind: 'For rent or purchase',
        specs: ['7 bedrooms', '48 hectares', 'Vineyard'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/1459a820-eb1a-4cd7-ae63-5b157f895c82.jpg',
        alt: 'A stone finca estate among olive trees at dusk with warm lit windows',
      },
      {
        title: 'Timber chalet above the village',
        location: 'Gstaad',
        kind: 'For rent · Winter',
        specs: ['6 suites', 'Ski-in', 'Resident chef'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/cd902aac-83b4-4d71-9cdb-a206496e6560.jpg',
        alt: 'A timber and stone alpine chalet in deep snow at blue hour with glowing windows',
      },
      {
        title: 'Lakeside villa with its own dock',
        location: 'Lake Como',
        kind: 'For purchase',
        specs: ['9 bedrooms', 'Private dock', 'Gardens'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/93d27e73-1843-4d12-bcf8-44b749a14ecd.jpg',
        alt: 'A historic lakeside villa with a private boat dock at dusk',
      },
      {
        title: 'Georgian townhouse off Grosvenor Square',
        location: 'London',
        kind: 'For purchase · Off-market',
        specs: ['6 floors', '1,050 m²', 'Mews garage'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/9ae57b8b-7e77-4407-b3f8-033ff7bba449.jpg',
        alt: 'A Georgian townhouse facade in Mayfair at night with glowing tall windows',
      },
      {
        title: 'Cliffside villa facing Es Vedrà',
        location: 'Ibiza',
        kind: 'For rent · May — October',
        specs: ['5 suites', 'Infinity pool', 'Sunset terrace'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/dd39f541-1a31-4b8c-b952-fc49f802e594.jpg',
        alt: 'A minimalist white cliffside villa with an infinity pool at sunset',
      },
      {
        title: 'Desert villa with a reflecting pool',
        location: 'Dubai',
        kind: 'For rent or purchase',
        specs: ['6 bedrooms', 'Private dunes', 'Stables'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/f0ddee16-68e0-427e-9266-1aa286fb39cf.jpg',
        alt: 'A modern low-slung desert villa with a reflecting pool at golden hour',
      },
      {
        title: 'Haussmann apartment facing the Tower',
        location: 'Paris',
        kind: 'For rent · Couture weeks',
        specs: ['4 bedrooms', '380 m²', 'Concierge'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/45a56123-d72d-4433-b5e9-ea30d4dccc63.jpg',
        alt: 'A Haussmann salon with tall windows overlooking the lit Eiffel Tower at dusk',
      },
    ],
    heldCount: '41 further residences held off-market',
    wallTitle: 'The collection is shown to members alone.',
    wallBody:
      'Addresses, prices and owners are never published. Leave a way to reach you and, should an introduction be appropriate, a partner will open the collection to you privately.',
  },
  {
    slug: 'mobility',
    title: 'Mobility',
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/19d08ad4-119a-4e9e-a291-21be36a9e686.jpg',
    heroAlt: 'A grand tourer on a marina quay beside a superyacht, a private jet in the sky above',
    tagline: 'By road, by sea, by air — already waiting.',
    statement: 'The car at the kerb, the yacht at anchor, the jet on the apron. Arranged before you ask.',
    intro:
      'Superluxury arranges movement without friction — chartered for a day or acquired for good. Through dealers, builders, owners and operators in our network, members reach cars, yachts and aircraft that are rarely offered, and never advertised.',
    waysHeading: 'To charter, or to own',
    ways: [
      {
        label: 'Concierge & charter',
        title: 'At your disposal',
        text: 'A chauffeured car in any city, a yacht for the week, a jet within hours — crewed, vetted and arranged on a single call.',
        points: ['Chauffeurs and cars on standby in every city we serve', 'Yacht charters with crews bound by NDA', 'Private aviation, wheels up within four hours'],
      },
      {
        label: 'Acquisition',
        title: 'To own',
        text: 'Allocations, pre-owned rarities and off-market vessels and aircraft — sourced, inspected and delivered quietly.',
        points: ['Allocations for limited and unreleased models', 'Yachts and aircraft offered before they are listed', 'Inspection, registration, crew and delivery'],
      },
    ],
    curationHeading: 'How an asset reaches you',
    curation: [
      { title: 'Sourced through the network', text: 'Builders, dealers, owners and operators offer us what they would never list.' },
      { title: 'Inspected and vetted', text: 'Every car, vessel and aircraft — and every crew — is checked by people we trust.' },
      { title: 'Offered to members first', text: 'Allocations and rare pieces go to members before anyone else hears of them.' },
    ],
    accessLine: 'The fleet is open to members, or by single introduction for a specific charter or acquisition.',
    collectionHeading: 'Currently held, privately',
    collectionNote: 'A glimpse of what is held for members. Specifications, prices and owners are shared on introduction.',
    listings: [
      {
        title: 'Grand tourer, one of twelve',
        location: 'Monaco',
        kind: 'For purchase · Allocation',
        specs: ['V12', '2026', 'Delivery in 8 weeks'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/ee00197e-c700-4b82-a838-78d8862ec8d2.jpg',
        alt: 'A dark grand tourer alone on an empty coastal road at dusk',
      },
      {
        title: 'Sixty-two metre motor yacht',
        location: 'The Riviera',
        kind: 'For charter · July',
        specs: ['12 guests', '22 crew', 'Helipad'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/73d3c627-e4f4-4a63-9b47-ef24e26efacd.jpg',
        alt: 'A white superyacht anchored alone in a deep turquoise bay, seen from above',
      },
      {
        title: 'Long-range private jet',
        location: 'Geneva',
        kind: 'For charter or purchase',
        specs: ['14 seats', '7,500 nm', 'Standby crew'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/36de4b1f-d9a9-4be3-92d4-81b9945c83e8.jpg',
        alt: 'A white private jet on an empty runway at dawn in soft mist',
      },
      {
        title: 'Twin-engine helicopter on standby',
        location: 'Côte d’Azur',
        kind: 'For charter · On call',
        specs: ['6 seats', 'Nice — Monaco 7 min', 'Pilot on standby'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/d1fead3b-f098-4cb7-bcf0-10d82858de52.jpg',
        alt: 'A black private helicopter on a clifftop helipad above the sea at golden hour',
      },
      {
        title: 'A 1964 grand tourer, fully restored',
        location: 'Tuscany',
        kind: 'For purchase · Collector',
        specs: ['Matching numbers', 'Concours history', 'Provenance'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/4b5b9659-1b2a-4294-89d6-8dd5f698e816.jpg',
        alt: 'A classic silver grand tourer in the courtyard of a Tuscan villa at dusk',
      },
      {
        title: 'Classic schooner under full sail',
        location: 'Mallorca',
        kind: 'For charter · Summer',
        specs: ['8 guests', '9 crew', '48 m'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/09b6435a-1901-4482-ac70-ef35a07cdd7f.jpg',
        alt: 'A classic wooden sailing yacht under full sail on deep blue sea, seen from above',
      },
      {
        title: 'Chauffeured saloon, any hour',
        location: 'Dubai · Hamburg · Warsaw',
        kind: 'At your disposal',
        specs: ['Armoured option', 'Vetted drivers', '24/7'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/9dee4af9-582c-4df6-a1a3-f9864f4e283d.jpg',
        alt: 'A chauffeur holding the rear door of a black sedan outside a grand hotel at night',
      },
      {
        title: 'Mahogany runabout for the season',
        location: 'Lake Como · Riviera',
        kind: 'For charter or purchase',
        specs: ['6 guests', 'Captained', 'Restored 2024'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/a19bbfd4-7699-40c1-93fb-c2ffc42ecefc.jpg',
        alt: 'A varnished mahogany speedboat cutting through turquoise water near a rocky coast',
      },
      {
        title: 'Hypercar, before its unveiling',
        location: 'Hamburg',
        kind: 'For purchase · Allocation',
        specs: ['1 of 40', 'Track package', 'Delivery 2027'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/b4ed41b6-2510-4294-8391-431e067dbae5.jpg',
        alt: 'An unbadged dark hypercar in a minimalist concrete garage',
      },
    ],
    heldCount: '63 further cars, yachts and aircraft held off-market',
    wallTitle: 'The fleet is shown to members alone.',
    wallBody:
      'Specifications, prices and owners are never published. Leave a way to reach you and, should an introduction be appropriate, a partner will open the fleet to you privately.',
  },
  {
    slug: 'retreats',
    title: 'Retreats',
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/06a12496-41e3-464f-bdde-19c68a4b548e.jpg',
    heroAlt: 'A minimalist retreat pavilion with a steaming stone bath overlooking a misty valley',
    tagline: 'Places to disappear. Held for you alone.',
    statement: 'The quietest places on earth, made quieter still.',
    intro:
      'We source the world’s most exceptional retreats — from celebrated public venues to private estates that have never welcomed a guest — and bring them together in one collection. Wellness, stillness and seclusion, arranged for members and those they choose to bring.',
    waysHeading: 'Public venues, private estates',
    ways: [
      {
        label: 'From public venues',
        title: 'Made private',
        text: 'The most sought-after retreats and resorts — taken entirely, closed to other guests, or opened after hours for you alone.',
        points: ['Full buyouts and closed wings', 'Practitioners and programmes chosen for you', 'Dates others are told are unavailable'],
      },
      {
        label: 'From private estates',
        title: 'Never listed',
        text: 'Islands, alpine lodges, desert camps and hidden houses opened only through our network — and only for members.',
        points: ['Estates opened for the first time', 'Staffed, secured and prepared in confidence', 'Shared with no one else that week'],
      },
    ],
    curationHeading: 'How a retreat reaches you',
    curation: [
      { title: 'Sourced far and wide', text: 'From celebrated sanctuaries to estates that have never had a guest.' },
      { title: 'Lived before it is offered', text: 'A partner stays at every retreat before it joins the collection.' },
      { title: 'Brought together for members', text: 'One collection, one partner, one quiet conversation to arrange it.' },
    ],
    accessLine: 'Retreats are reserved for members, or by single introduction for a specific stay.',
    collectionHeading: 'Currently held, privately',
    collectionNote: 'A glimpse of the collection. Locations, dates and hosts are shared on introduction.',
    listings: [
      {
        title: 'Glass pavilion in a snowbound forest',
        location: 'The Alps',
        kind: 'Private estate · Winter',
        specs: ['6 guests', 'Thermal pool', 'Resident physician'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/981e9733-1d35-41ac-a2a2-846d5e47a03e.jpg',
        alt: 'A timber and glass pavilion with a steaming stone pool in a snowy forest at dusk',
      },
      {
        title: 'Lantern camp among the dunes',
        location: 'Arabian Desert',
        kind: 'Buyout · 5 nights',
        specs: ['10 guests', 'Astronomer', 'Falconry'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/067f0aa9-7965-46ba-bbbb-ddfefa8c54bb.jpg',
        alt: 'Lantern-lit pavilions among desert dunes at blue hour',
      },
      {
        title: 'Island house above a hidden cove',
        location: 'Mediterranean',
        kind: 'Private estate · Summer',
        specs: ['4 suites', 'Reached by sea', 'Private chef'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/5d01fe68-faa3-4a50-b4ea-fd96742556ef.jpg',
        alt: 'A white villa terrace with a lone lounge chair above a calm cove at sunrise',
      },
      {
        title: 'Ryokan with a private onsen',
        location: 'Kyoto',
        kind: 'Buyout · Autumn',
        specs: ['8 guests', 'Kaiseki chef', 'Tea master'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/23ce8191-c0d0-4235-92f0-7e6f26bd5886.jpg',
        alt: 'A secluded ryokan with a steaming stone onsen among autumn maples at dusk',
      },
      {
        title: 'Private camp above the savannah',
        location: 'Kenya',
        kind: 'Private estate · Season',
        specs: ['6 tents', 'Private guide', 'Bush flights'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/e6388288-a0a4-45a6-a05c-527eb4c2348e.jpg',
        alt: 'A luxury safari tent lodge on a wooden deck over the savannah at sunset',
      },
      {
        title: 'The only villa on the lagoon',
        location: 'Maldives',
        kind: 'Buyout · Year-round',
        specs: ['2 guests', 'Overwater', 'Seaplane'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/5cd1b213-fd64-4343-a191-012bde56db0c.jpg',
        alt: 'A single overwater villa on a turquoise lagoon at sunrise, seen from above',
      },
      {
        title: 'Hilltop monastery, never opened to guests',
        location: 'Tuscany',
        kind: 'Never listed · Summer',
        specs: ['12 guests', 'Vineyard', 'Chapel'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/e025363a-e0ad-4990-9adb-0aaf8e38db13.jpg',
        alt: 'A restored Tuscan hilltop estate with a cypress alley and pool at golden hour',
      },
      {
        title: 'Glass lodge beneath the aurora',
        location: 'Lapland',
        kind: 'Buyout · Winter',
        specs: ['4 guests', 'Aurora guide', 'Sauna'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/ce39bf26-010d-4136-ae2e-6f5b32d540e2.jpg',
        alt: 'A glass-roofed lodge in a snowy forest beneath the northern lights',
      },
      {
        title: 'Island estate on the Aegean',
        location: 'Cyclades',
        kind: 'Private estate · June — September',
        specs: ['7 suites', 'Private jetty', 'Yacht on call'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/9417d444-1fb9-4157-ba1d-f6ee8c6340d3.jpg',
        alt: 'A whitewashed island estate with a sea-view terrace at blue hour',
      },
    ],
    heldCount: '27 further retreats held off-market',
    wallTitle: 'The retreats are shown to members alone.',
    wallBody:
      'Locations, dates and hosts are never published. Leave a way to reach you and, should an introduction be appropriate, a partner will open the collection to you privately.',
  },
  {
    slug: 'sl-club',
    title: 'SL Club',
    heroImage: 'https://cdn.prod.website-files.com/67dd5fcccda09c76d473a2ef/6a462542756b4af58325f77b_Rectangle%20922.avif',
    heroAlt: 'SL Club — members gathered at a private Superluxury club setting',
    tagline: 'A circle kept small. Evenings kept quiet.',
    statement: 'Some rooms are worth entering only for who else is in them.',
    intro:
      'SL Club gathers the best evenings of the year in one place — the finest seats of public occasions alongside private dinners, viewings and salons held only for members. Communication is members-only, through a single private channel. Nothing is announced; everything is extended.',
    waysHeading: 'Public occasions, private rooms',
    ways: [
      {
        label: 'From public stages',
        title: 'The best seat',
        text: 'Grand Prix terraces, art fairs, fashion weeks and premieres — sourced from the occasions everyone hears of, and arranged so you are never one of the crowd.',
        points: ['Terraces, boxes and tables no one else can book', 'Private arrival and departure', 'Introductions made on the night'],
      },
      {
        label: 'Members only',
        title: 'Behind closed doors',
        text: 'Suppers, viewings and salons held only for members and their guests — never photographed, never repeated.',
        points: ['Dinners for twelve, in places that do not open', 'Private viewings before a collection is shown', 'One private channel for every invitation'],
      },
    ],
    curationHeading: 'How an evening reaches you',
    curation: [
      { title: 'Sourced from every side', text: 'Public venues, private houses and partners open their doors to the club.' },
      { title: 'Composed with care', text: 'Each evening is kept small, and every guest list is considered.' },
      { title: 'Extended privately', text: 'Invitations arrive through the members’ channel — and nowhere else.' },
    ],
    accessLine: 'SL Club is open to members, and to their guests by invitation.',
    collectionHeading: 'This season, privately',
    collectionNote: 'A glimpse of the calendar. Venues, dates and guest lists are shared with members alone.',
    listings: [
      {
        title: 'Supper for twelve in a closed salon',
        location: 'Paris',
        kind: 'Members’ dinner',
        specs: ['12 guests', 'Black tie', 'Chef’s table'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/f22ecf94-4a5d-4546-961a-686e8f9972e9.jpg',
        alt: 'A candlelit private dining room with a long table set for twelve',
      },
      {
        title: 'Harbour terrace on race weekend',
        location: 'Monaco',
        kind: 'Public occasion, made private',
        specs: ['3 days', 'Paddock access', 'Yacht transfers'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/4c8c01ce-0b10-40e3-9a77-7d2beee62739.jpg',
        alt: 'Guests in evening wear on a rooftop terrace above a lit harbour at night',
      },
      {
        title: 'A viewing before the collection is shown',
        location: 'Basel',
        kind: 'Private viewing',
        specs: ['Evening', '20 guests', 'With the artist'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/cae069d8-ff6b-49e6-94cd-6d0f2020d709.jpg',
        alt: 'Two silhouetted guests before a spotlit painting in a dark gallery',
      },
      {
        title: 'A box on opening night',
        location: 'Vienna',
        kind: 'Public occasion, made private',
        specs: ['6 guests', 'Backstage', 'Supper after'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/bb55534a-bb86-4a81-a8ea-8973d9b082cb.jpg',
        alt: 'An empty private opera box with red velvet seats overlooking a lit stage',
      },
      {
        title: 'A cellar opened for one evening',
        location: 'Bordeaux',
        kind: 'Members’ dinner',
        specs: ['10 guests', 'Vertical tasting', 'With the owner'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/3b1ad854-5cec-469e-b2b8-2295b9a690db.jpg',
        alt: 'A candlelit tasting table in a vaulted stone wine cellar',
      },
      {
        title: 'The final at the polo, from the marquee',
        location: 'Dubai',
        kind: 'Public occasion, made private',
        specs: ['Afternoon', 'Pitchside', 'Meet the players'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/d77b1e79-d8d7-44de-9615-87449339ec17.jpg',
        alt: 'A polo match at golden hour seen from an elegant white marquee',
      },
      {
        title: 'A night aboard, off Saint-Tropez',
        location: 'Saint-Tropez',
        kind: 'Members’ evening',
        specs: ['40 guests', 'Tender service', 'No photographs'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/493d92cf-e2a4-4923-bd5d-29c66cf52b74.jpg',
        alt: 'Guests in evening wear on a superyacht deck at night, silhouetted by candlelight',
      },
      {
        title: 'A couture show for thirty',
        location: 'Paris',
        kind: 'Private viewing',
        specs: ['Front row', 'Fitting after', 'Salon'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/411a3879-1132-499e-8ca3-62a396fde316.jpg',
        alt: 'A model silhouetted on the runway of an intimate couture show in a chandeliered salon',
      },
      {
        title: 'Supper in a hut above the pistes',
        location: 'Courchevel',
        kind: 'Members’ dinner',
        specs: ['14 guests', 'Snowcat up', 'Fireside'],
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/96fcb92d-9e2a-48d3-ad4c-da72ca564a8f.jpg',
        alt: 'A candlelit dinner table in a mountain hut with snowy peaks through the window',
      },
    ],
    heldCount: '18 further evenings on this season’s calendar',
    wallTitle: 'The calendar is shared with members alone.',
    wallBody:
      'Venues, dates and guest lists are never published. Leave a way to reach you and, should an introduction be appropriate, an invitation will be extended privately.',
  },
];
